// One card per sub-feature, each carrying its reference.
//
// SF2 and SF3 wait on the Amazon Ads API. They render locked and say what is
// missing — never as a zero, because a zero here would be a claim about the
// seller's advertising rather than about our data.
import React from 'react';
import { FiArrowRight, FiLock } from 'react-icons/fi';
import s from '../SearchAttribution.module.css';
import { money, num, pct } from '../format';

function Body({ card }) {
  switch (card.key) {
    case 'revenue': {
      if (!card.top?.length) return null;
      const total = (card.allocated || 0) + (card.unallocated || 0);
      return (
        <>
          {total > 0 && (
            <>
              <div className={s.cardBar}>
                <span className={`${s.cardBarSeg} ${s.mix_generic}`}
                      style={{ width: `${(card.allocated / total) * 100}%` }}
                      title={`allocated to terms: ${money(card.allocated)}`} />
                <span className={`${s.cardBarSeg} ${s.mix_competitor}`}
                      style={{ width: `${(card.unallocated / total) * 100}%` }}
                      title={`unallocated: ${money(card.unallocated)}`} />
              </div>
              <ul className={s.cardList}>
                <li>
                  <span className={s.cardTerm}>
                    <span className={`${s.cardDot} ${s.mix_generic}`} /> allocated to terms
                  </span>
                  <span className={s.cardFigure}>{money(card.allocated)}</span>
                </li>
                <li>
                  <span className={s.cardTerm}>
                    <span className={`${s.cardDot} ${s.mix_competitor}`} /> unallocated
                  </span>
                  <span className={s.cardFigure}>{money(card.unallocated)}</span>
                </li>
              </ul>
            </>
          )}
          <ul className={s.cardList}>
            {card.top.map((p) => (
              <li key={p.asin}>
                <span className={s.cardTerm}>{p.asin}</span>
                <span className={s.cardFigure}>
                  {pct(p.share, 0)} <span className={s.muted}>of {money(p.total_revenue ?? null)}</span>
                </span>
              </li>
            ))}
            <li className={s.cardNote}>
              The per-product figure is Amazon&apos;s. The per-term split is ours, scaled so
              the parts sum to it.
            </li>
          </ul>
        </>
      );
    }

    case 'sessions':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((d) => (
            <li key={d.asin}>
              <span className={s.cardTerm}>{d.asin}</span>
              <span className={d.flagged ? s.down : s.cardFigure}>
                {pct(d.share, 0)} <span className={s.muted}>on “{d.term}”</span>
              </span>
            </li>
          ))}
          <li className={s.cardNote}>
            Dependency is a risk flag, not a ranking — a product can live on search across
            many terms, or barely use search and hang on one.
          </li>
        </ul>
      );

    case 'long_tail': {
      const seg = card.segment;
      if (!seg) return null;
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Share of allocated revenue</span>
            <span className={s.cardFigure}>{pct(card.share_of_allocated_revenue, 0)}</span>
          </li>
          <li>
            <span className={s.cardTerm}>Share of search clicks</span>
            <span className={s.cardFigure}>{pct(card.share_of_search_clicks, 0)}</span>
          </li>
          <li>
            <span className={s.cardTerm}>Conversion</span>
            <span className={s.cardFigure}>
              {pct(seg.conversion, 2)} <span className={s.muted}>on {num(seg.clicks)} clicks</span>
            </span>
          </li>
          <li className={s.cardNote}>
            Margin cannot be shown: no cost of goods is stored for this account. Revenue only,
            and said so rather than a margin invented.
          </li>
        </ul>
      );
    }

    case 'roi':
    case 'split':
      return (
        <ul className={s.cardList}>
          <li className={s.cardNote}>
            This section stays absent until Amazon Ads is connected. It will not appear as a
            zero in the meantime.
          </li>
        </ul>
      );

    default:
      return null;
  }
}

export default function FeatureCards({ data, loading, onOpen }) {
  if (loading && !data) {
    return (
      <div className={s.cardGrid}>
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className={s.cardSkeleton} />)}
      </div>
    );
  }
  if (!data?.cards?.length) return null;

  return (
    <div className={s.cardGrid}>
      {data.cards.map((card) => {
        const clickable = card.state !== 'unavailable';
        const open = () => onOpen(card.tab);
        return (
          <section key={card.key}
                   className={`${s.card} ${s[`card_${card.state}`]} ${clickable ? s.cardOpen : ''}`}
                   onClick={clickable ? open : undefined}
                   role={clickable ? 'button' : undefined}
                   tabIndex={clickable ? 0 : undefined}
                   onKeyDown={clickable ? (e) => {
                     if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                   } : undefined}>
            <header className={s.cardHead}>
              <span className={s.cardRef}>{card.ref}</span>
              {clickable ? <FiArrowRight className={s.cardGo} /> : <FiLock className={s.cardLock} />}
            </header>
            <h3 className={s.cardTitle}>{card.title}</h3>
            <p className={s.cardQuestion}>{card.question}</p>
            <div className={s.cardHeadline}>
              <span className={s.cardValue}>{card.headline}</span>
              {card.unit && <span className={s.cardUnit}>{card.unit}</span>}
            </div>
            <p className={s.cardDetail}>{card.detail}</p>
            <Body card={card} />
          </section>
        );
      })}
    </div>
  );
}
